import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  SESSION_EXPIRED_EVENT,
  SESSION_EXPIRED_MESSAGE,
  type SessionExpiredDetail,
} from '@/constants/auth-events';
import { ROUTES } from '@/constants/routes';
import userProfileService from '@/services/user-profile-service';

import { useCustomToast } from '../hooks/use-toast';
import { useGetUserProfile } from '../hooks/use-user-profile';
import {
  type ClaimedAgentSuite,
  type UserBasicInfoPayload,
  type UserInfo,
} from '../types/user';
import { clearAuthHint, setAuthHint } from '../utils/auth-hint';
import { useTenant } from './tenant-context';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isError: boolean;
  isUserLoading: boolean;
  isLoadingUserInitials: boolean;
  isOnline: boolean;
  isKeycloakReady: boolean; // True when Keycloak has finished initializing
  isKeycloakAuthenticated: boolean; // True when Keycloak says user is authenticated
  user: UserInfo | null;
  claimedSuites: ClaimedAgentSuite[] | null;
  login: (redirectUri?: string) => void;
  logout: () => Promise<boolean>;
  getUserInitials: () => string;
}

const AuthContext = createContext<AuthContextType | null>(null);

const publicAuthFallback: AuthContextType = {
  isAuthenticated: false,
  isLoading: false,
  isError: false,
  isUserLoading: false,
  isLoadingUserInitials: false,
  isOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
  isKeycloakReady: false,
  isKeycloakAuthenticated: false,
  user: null,
  claimedSuites: null,
  login: (redirectUri?: string) => {
    if (typeof window === 'undefined') return;
    const target = new URL(ROUTES.LOGIN, window.location.origin);
    if (redirectUri) {
      target.searchParams.set('redirect_uri', redirectUri);
    }
    window.location.href = target.toString();
  },
  logout: async () => false,
  getUserInitials: () => 'U',
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context ?? publicAuthFallback;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const queryClient = useQueryClient();
  const [isReady, setIsReady] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { setTenantId } = useTenant();
  const sessionExpiryHandledRef = useRef(false);
  const logoutStartedRef = useRef(false);
  const { errorToast } = useCustomToast();
  const isKeycloakAuthenticated = keycloak?.authenticated ?? false;
  // Network connectivity detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const {
    data: userData,
    isLoading: userDataLoading,
    isError,
  } = useGetUserProfile<UserBasicInfoPayload>({
    onSuccess: (data: UserBasicInfoPayload) => {
      if (data && data.userInfo) {
        // Find the active tenant from the user's tenants
        if (data.userInfo.tenant?.tenantId) {
          setTenantId(data.userInfo.tenant.tenantId);
        }
      }
    },
    enabled: initialized && keycloak.authenticated && isOnline,
    retry: isOnline ? 3 : 0,
    refetchOnReconnect: true,
    networkMode: isOnline ? 'online' : 'offlineFirst',
  });

  useEffect(() => {
    if (initialized) {
      setIsReady(true);
      if (keycloak.authenticated) {
        // Real Keycloak auth owns the session. The hint only lets public
        // headers render the same user state without blocking the page.
        setAuthHint({ idTokenHint: keycloak.idToken });
      } else {
        clearAuthHint();
      }
    }
  }, [initialized, keycloak.authenticated, keycloak.idToken]);

  // Set tenant ID when user data is loaded
  useEffect(() => {
    if (userData?.userInfo?.tenant?.tenantId) {
      setTenantId(userData.userInfo.tenant.tenantId);
    }
  }, [userData?.userInfo?.tenant?.tenantId, setTenantId]);

  useEffect(() => {
    if (isKeycloakAuthenticated && userData?.userInfo) {
      sessionExpiryHandledRef.current = false;
      // Keep public header display data in sync after the protected profile
      // request succeeds; protected access still depends on Keycloak.
      setAuthHint({
        firstName: userData.userInfo.firstName,
        lastName: userData.userInfo.lastName,
        email: userData.userInfo.email,
        profilePicture: userData.userInfo.profilePicture || undefined,
        idTokenHint: keycloak.idToken,
      });
    }
  }, [isKeycloakAuthenticated, keycloak.idToken, userData?.userInfo]);

  const login = useCallback(
    (redirectUri?: string) => {
      // Only use redirectUri if provided, otherwise let Keycloak handle default redirect
      if (redirectUri) {
        keycloak.login({
          redirectUri,
        });
      } else {
        keycloak.login({
          redirectUri: `${window.location.origin}/dashboard`,
        });
      }
    },
    [keycloak]
  );

  const clearClientSession = useCallback(() => {
    void queryClient.cancelQueries();
    queryClient.clear();
    setTenantId(null);

    // Clear all tenant-related localStorage data
    try {
      localStorage.removeItem('agentous_tenant_id');
      localStorage.removeItem('agentous_active_agent');
    } catch (error) {
      console.error('Error clearing localStorage on logout:', error);
    }
    clearAuthHint();
  }, [queryClient, setTenantId]);

  const redirectToIdentityLogout = useCallback(() => {
    clearClientSession();

    void keycloak.logout({
      redirectUri: `${window.location.origin}/`,
    });
  }, [clearClientSession, keycloak]);

  const logout = useCallback(async () => {
    if (logoutStartedRef.current) return false;
    logoutStartedRef.current = true;

    try {
      await keycloak.updateToken(30);
      if (!keycloak.token) {
        throw new Error('No access token is available for logout.');
      }

      await userProfileService.logout(keycloak.token);
      clearClientSession();
      keycloak.clearToken();
      window.location.href = ROUTES.HOME;
      return true;
    } catch (error) {
      console.error('Failed to revoke all Agentous sessions:', error);
      logoutStartedRef.current = false;
      return false;
    }
  }, [clearClientSession, keycloak]);

  const handleSessionExpiration = useCallback(
    (message?: string) => {
      if (sessionExpiryHandledRef.current) return;
      sessionExpiryHandledRef.current = true;

      errorToast(message || SESSION_EXPIRED_MESSAGE);
      redirectToIdentityLogout();
    },
    [errorToast, redirectToIdentityLogout]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleSessionExpiredEvent = (event: Event) => {
      const customEvent = event as CustomEvent<SessionExpiredDetail>;
      handleSessionExpiration(customEvent.detail?.reason);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpiredEvent);
    return () => {
      window.removeEventListener(
        SESSION_EXPIRED_EVENT,
        handleSessionExpiredEvent
      );
    };
  }, [handleSessionExpiration]);

  // Token expiration is now handled in axios interceptors when 401 responses are received
  // No need for Keycloak event handlers as they don't reliably fire

  const getUserInitials = (): string => {
    if (!userData?.userInfo) return 'U';

    const { firstName, lastName } = userData.userInfo;

    if (firstName && lastName) {
      return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
    }

    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }

    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }

    if (userData.userInfo.email) {
      return userData.userInfo.email.charAt(0).toUpperCase();
    }

    return 'U';
  };

  // Determine loading state more precisely:
  // - If Keycloak isn't ready, we're loading
  // - If Keycloak is ready but user is NOT authenticated, we're NOT loading (should redirect)
  // - If Keycloak is ready and user IS authenticated, we're loading only if user data is still loading
  const isLoadingAuth =
    !isReady ||
    (isKeycloakAuthenticated && userDataLoading && !userData && isOnline);

  const value: AuthContextType = {
    isAuthenticated:
      initialized && (keycloak.authenticated ?? false) && !!userData,
    isLoading: isLoadingAuth,
    isUserLoading: userDataLoading,
    isLoadingUserInitials: userDataLoading,
    isError: isError || !isOnline,
    isOnline,
    isKeycloakReady: isReady,
    isKeycloakAuthenticated,
    user: userData?.userInfo || null,
    claimedSuites: userData?.userInfo?.tenant?.claimedAgentSuites || null,
    login,
    logout,
    getUserInitials,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
